import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { DownloadSimple, FilePdf, Table as TableIcon } from "@phosphor-icons/react";
import { ReportScheduleDialog } from "./ReportScheduleDialog";

const month0 = () => new Date().toISOString().slice(0, 7);
const fmtCell = (v) => (v === null || v === undefined || v === "" ? "—" : typeof v === "boolean" ? (v ? "Yes" : "No") : typeof v === "number" ? v.toLocaleString("en-IN") : String(v));

export function DetailedReports({ params }) {
  const [reports, setReports] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [key, setKey] = useState("attendance");
  const [f, setF] = useState({ month: month0(), from: `${new Date().getFullYear()}-01-01`, to: new Date().toISOString().slice(0, 10), employee_id: "" });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/hr-reports").then(({ data }) => setReports(data)).catch(() => {});
    api.get("/employees", { params }).then(({ data }) => setEmployees(data)).catch(() => {});
    // eslint-disable-next-line
  }, [params.tenant_id]);

  const def = reports.find((r) => r.key === key) || { filters: [] };
  const query = () => {
    const q = { ...params };
    if (def.filters.includes("month")) q.month = f.month;
    if (def.filters.includes("from")) { q.from = f.from; q.to = f.to; }
    if (def.filters.includes("employee") && f.employee_id) q.employee_id = f.employee_id;
    return q;
  };
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.get(`/hr-reports/${key}`, { params: query() }); setData(data); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  useEffect(() => { if (reports.length) run(); /* eslint-disable-next-line */ }, [key, reports.length]);

  const download = async () => {
    try {
      const { data: blob, headers } = await api.get(`/hr-reports/${key}`, { params: { ...query(), format: "csv" }, responseType: "blob" });
      const name = (headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || `${key}.csv`;
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openPdf = async (path) => {
    try { const { data } = await api.get(path.replace(/^\/api/, ""), { responseType: "blob" }); window.open(URL.createObjectURL(data), "_blank"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const cols = (data?.columns || []).filter((c) => c !== "pdf" && c !== "payslip_id");
  return (
    <div className="space-y-4" data-testid="detailed-reports">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
        <div className="lg:col-span-1 border border-zinc-200 bg-white rounded-sm p-2 space-y-0.5" data-testid="report-picker">
          {reports.length === 0 && <div className="text-xs text-zinc-500 p-3" data-testid="report-picker-empty">Detailed reports are available to HR, admins and finance.</div>}
          {reports.map((r) => (
            <button key={r.key} onClick={() => { if (r.key === key) { run(); return; } setKey(r.key); setData(null); }} className={`w-full text-left text-xs px-3 py-2 rounded-sm transition-colors ${key === r.key ? "bg-adb-navy text-white" : "hover:bg-zinc-50 text-zinc-700"}`} data-testid={`report-pick-${r.key}`}>{r.label}</button>
          ))}
        </div>
        <div className="lg:col-span-3 space-y-3">
          <div className="border border-zinc-200 bg-white rounded-sm p-4 flex flex-wrap items-end gap-3" data-testid="report-filters">
            {def.filters.includes("month") && <div className="space-y-1"><Label className="text-xs">Month</Label><Input type="month" value={f.month} onChange={(e) => setF({ ...f, month: e.target.value })} className="rounded-sm h-9" data-testid="report-month" /></div>}
            {def.filters.includes("from") && <>
              <div className="space-y-1"><Label className="text-xs">From</Label><Input type="date" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} className="rounded-sm h-9" data-testid="report-from" /></div>
              <div className="space-y-1"><Label className="text-xs">To</Label><Input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} className="rounded-sm h-9" data-testid="report-to" /></div>
            </>}
            {def.filters.includes("employee") && (
              <div className="space-y-1 min-w-[220px]">
                <Label className="text-xs">Employee</Label>
                <Select value={f.employee_id || "__all"} onValueChange={(v) => setF({ ...f, employee_id: v === "__all" ? "" : v })}>
                  <SelectTrigger className="rounded-sm h-9" data-testid="report-employee"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="__all">All employees</SelectItem>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} · {e.employee_code}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <Button onClick={run} disabled={busy} className="btn-zenith rounded-sm h-9" data-testid="report-run"><TableIcon size={14} className="mr-1" /> {busy ? "Loading…" : "View report"}</Button>
            <Button variant="outline" onClick={download} disabled={!data?.count} className="rounded-sm h-9" data-testid="report-download"><DownloadSimple size={14} className="mr-1" /> Download CSV</Button>
            <ReportScheduleDialog params={params} reports={reports} />
            {data && <div className="text-xs text-zinc-500 ml-auto" data-testid="report-count">{data.count} row(s)</div>}
          </div>
          <div className="border border-zinc-200 bg-white rounded-sm overflow-auto max-h-[60vh]" data-testid="report-table">
            {!data ? <div className="p-8 text-center text-sm text-zinc-500">Loading…</div> : data.count === 0 ? <div className="p-8 text-center text-sm text-zinc-500">No records for the selected filters.</div> : (
              <table className="hr-table text-xs">
                <thead><tr>{cols.map((c) => <th key={c} className="whitespace-nowrap">{c.replace(/_/g, " ")}</th>)}{key === "payslips" && <th></th>}</tr></thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={i} data-testid={`report-row-${i}`}>
                      {cols.map((c) => <td key={c} className={`whitespace-nowrap ${typeof r[c] === "number" ? "font-mono-plex" : ""}`}>{fmtCell(r[c])}</td>)}
                      {key === "payslips" && <td><button onClick={() => openPdf(r.pdf)} className="text-xs text-emerald-700 hover:underline inline-flex items-center gap-1" data-testid={`report-payslip-pdf-${r.payslip_id}`}><FilePdf size={14} /> PDF</button></td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
